import { Injectable, inject } from '@angular/core';
import { Observable, map, of, throwError, switchMap } from 'rxjs';

import { ApiService } from './api.service';
import { VinService } from './vin.service';
import { SessionService } from './session.service';
import {
  ExceptionFormPrefill,
  VinAddStatus,
  VinRequestStatusData,
  ApiEnvelope,
  SubmitExceptionRequest,
  SubmitExceptionResponseData,
} from '@core/models';

/**
 * Service for consumer exception submission flow.
 * Spec 012: loads pre-populated form data from the FAILED_INELIGIBLE vin_add_request
 * and the active consumer session.
 */
@Injectable({ providedIn: 'root' })
export class ExceptionService {
  private readonly api = inject(ApiService);
  private readonly vinService = inject(VinService);
  private readonly sessionService = inject(SessionService);

  /**
   * Build the read-only form prefill for an exception request.
   *
   * Fails if:
   * - No active consumer session
   * - vinAddRequestId cannot be resolved
   * - Underlying vin_add_request is not in FAILED_INELIGIBLE state
   */
  loadPrefill(vinAddRequestId: string): Observable<ExceptionFormPrefill> {
    if (!this.sessionService.isAuthenticated()) {
      return throwError(() => new Error('NO_SESSION'));
    }
    if (!vinAddRequestId) {
      return throwError(() => new Error('MISSING_REQUEST_ID'));
    }

    return this.vinService.getStatus(vinAddRequestId).pipe(
      switchMap((res: ApiEnvelope<VinRequestStatusData>) => {
        if (!res.success || !res.data) {
          return throwError(() => new Error('REQUEST_NOT_FOUND'));
        }
        if (res.data.status !== VinAddStatus.FAILED_INELIGIBLE) {
          return throwError(() => new Error('REQUEST_NOT_ELIGIBLE_FOR_EXCEPTION'));
        }
        return of(this.buildPrefill(res.data));
      }),
      map((p) => p),
    );
  }

  /**
   * Submit an exception for the FAILED_INELIGIBLE vin_add_request.
   * Calls POST /exceptions and unwraps the envelope into the response data.
   */
  submit(input: SubmitExceptionRequest): Observable<SubmitExceptionResponseData> {
    if (!input.secondaryVin) {
      return throwError(() => new Error('MISSING_SECONDARY_VIN'));
    }
    if (!input.customerFirstName?.trim()) {
      return throwError(() => new Error('MISSING_CUSTOMER_FIRST_NAME'));
    }
    if (!input.customerLastName?.trim()) {
      return throwError(() => new Error('MISSING_CUSTOMER_LAST_NAME'));
    }
    const source = input.source ?? this.sessionService.getSessionSource();
    const emailTrimmed = input.customerEmail?.trim();
    const payload: SubmitExceptionRequest = {
      secondaryVin: input.secondaryVin.toUpperCase(),
      customerFirstName: input.customerFirstName.trim(),
      customerLastName: input.customerLastName.trim(),
      ...(emailTrimmed && { customerEmail: emailTrimmed.toLowerCase() }),
      source,
    };
    if (input.vinAddRequestId) {
      payload.vinAddRequestId = input.vinAddRequestId;
    }
    if (input.primaryVinClassing) {
      payload.primaryVinClassing = input.primaryVinClassing;
    }
    if (input.secondaryVinClassing) {
      payload.secondaryVinClassing = input.secondaryVinClassing;
    }
    return this.api.post<SubmitExceptionResponseData>('/exceptions', payload).pipe(
      switchMap((res: ApiEnvelope<SubmitExceptionResponseData>) => {
        if (!res.success || !res.data) {
          return throwError(() => new Error(res.error?.code ?? 'SUBMIT_FAILED'));
        }
        return of(res.data);
      }),
    );
  }

  private buildPrefill(data: VinRequestStatusData): ExceptionFormPrefill {
    const summary = this.sessionService.contractSummary();
    const authForm = this.sessionService.getAuthFormValues();
    const customerName = authForm?.lastName ? this.titleCase(authForm.lastName) : undefined;

    return {
      vinAddRequestId: data.requestId,
      eligibilityReasonCode: data.eligibilityReasonCode,
      contractRef: summary?.primaryVinMasked,
      customerName,
      primaryVinMasked: summary?.primaryVinMasked,
      secondaryVin: data.vin ?? '',
      secondaryDecoded: data.decoded,
    };
  }

  private titleCase(value: string): string {
    return value.toLowerCase().replace(/\b\w/g, (c) => c.toUpperCase());
  }
}
